import { useState, useEffect } from 'react';
import { X, Sparkles, Loader, Check, Music } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';

interface YouTubeSuggestion {
  videoId: string | null;
  title: string;
  description: string;
  thumbnail: string;
  channelTitle: string;
  youtubeQuery: string;
  mediaUrl: string;
  aiReason: string;
}

interface AIMusicSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (suggestions: YouTubeSuggestion[]) => void;
  locationName?: string;
  latitude?: number;
  longitude?: number;
}

export function AIMusicSuggestionModal({
  isOpen,
  onClose,
  onSelect,
  locationName,
  latitude,
  longitude,
}: AIMusicSuggestionModalProps) {
  const { language } = useLanguage();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<YouTubeSuggestion[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setSuggestions([]);
    setSelectedIds(new Set());

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-suggest-music`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query,
            locationName,
            latitude,
            longitude,
            radius: 1000,
          }),
        }
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const aiSuggestions = data.suggestions || [];

      // Fetch YouTube data for each suggestion in the frontend
      const youtubeKey = import.meta.env.VITE_YOUTUBE_API_KEY;

      if (youtubeKey) {
        const enrichedSuggestions = await Promise.all(
          aiSuggestions.map(async (suggestion: YouTubeSuggestion) => {
            if (suggestion.videoId) {
              return suggestion; // Already has video data
            }

            try {
              const searchQuery = suggestion.youtubeQuery || suggestion.title;
              const ytUrl = `https://www.googleapis.com/youtube/v3/search?part=snippet&type=video&videoCategoryId=10&maxResults=1&q=${encodeURIComponent(searchQuery)}&key=${youtubeKey}`;

              const ytResponse = await fetch(ytUrl);
              const ytData = await ytResponse.json();

              if (ytData.items && ytData.items.length > 0) {
                const item = ytData.items[0];
                return {
                  ...suggestion,
                  videoId: item.id.videoId,
                  title: item.snippet.title,
                  description: item.snippet.description,
                  thumbnail: item.snippet.thumbnails.high?.url || item.snippet.thumbnails.medium?.url || item.snippet.thumbnails.default?.url || `https://img.youtube.com/vi/${item.id.videoId}/mqdefault.jpg`,
                  channelTitle: item.snippet.channelTitle,
                  mediaUrl: `https://www.youtube.com/watch?v=${item.id.videoId}`,
                };
              }
            } catch (err) {
              console.error('YouTube fetch error:', err);
            }

            return suggestion;
          })
        );

        setSuggestions(enrichedSuggestions);
      } else {
        setSuggestions(aiSuggestions);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (index: number) => {
    const newSelected = new Set(selectedIds);
    const id = `suggestion-${index}`;
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleConfirm = async () => {
    const selected = suggestions.filter((_, index) => selectedIds.has(`suggestion-${index}`));

    const updatedSuggestions = [];
    for (const suggestion of selected) {
      if (!suggestion.videoId) {
        const youtubeUrl = prompt(
          language === 'ko'
            ? `"${suggestion.title}"의 YouTube URL을 입력하세요:\n(예: https://www.youtube.com/watch?v=VIDEO_ID)`
            : `Enter YouTube URL for "${suggestion.title}":\n(e.g., https://www.youtube.com/watch?v=VIDEO_ID)`
        );

        if (youtubeUrl && youtubeUrl.trim()) {
          const videoIdMatch = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
          if (videoIdMatch) {
            const videoId = videoIdMatch[1];
            updatedSuggestions.push({
              ...suggestion,
              videoId,
              mediaUrl: `https://www.youtube.com/watch?v=${videoId}`,
              thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
            });
          } else {
            alert(language === 'ko' ? '유효하지 않은 YouTube URL입니다.' : 'Invalid YouTube URL');
            return;
          }
        } else {
          return;
        }
      } else {
        updatedSuggestions.push(suggestion);
      }
    }

    onSelect(updatedSuggestions);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {language === 'ko' ? 'AI 음악 추천' : 'AI Music Suggestions'}
                </h2>
                <p className="text-sm text-gray-600">
                  {language === 'ko'
                    ? '원하는 음악 스타일을 설명하세요'
                    : 'Describe the music style you want'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {locationName && (
            <div className="bg-teal-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700">
                {language === 'ko' ? '위치: ' : 'Location: '}
                <span className="font-semibold">{locationName}</span>
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={
                language === 'ko'
                  ? '예: 70년대 포크송, 차분한 재즈 피아노, 신나는 K-pop'
                  : 'e.g., 70s folk songs, calm jazz piano, upbeat K-pop'
              }
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={loading}
            />
            <button
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              {language === 'ko' ? '검색' : 'Search'}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          )}

          {!loading && suggestions.length === 0 && !error && (
            <div className="text-center py-12 text-gray-500">
              <Sparkles className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>
                {language === 'ko'
                  ? '음악 스타일을 입력하고 검색 버튼을 클릭하세요'
                  : 'Enter a music style and click search'}
              </p>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                {language === 'ko'
                  ? `${suggestions.length}개의 추천 음악 • 원하는 음악을 선택하세요 (복수 선택 가능)`
                  : `${suggestions.length} suggestions • Select the music you want (multiple selection allowed)`}
              </p>
              {suggestions.map((suggestion, index) => {
                const hasValidVideo = suggestion.videoId && suggestion.videoId !== null;
                return (
                <button
                  key={`suggestion-${index}`}
                  onClick={() => toggleSelection(index)}
                  className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 transition-all ${
                    selectedIds.has(`suggestion-${index}`)
                      ? 'border-purple-500 bg-purple-50 hover:shadow-md'
                      : hasValidVideo
                      ? 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                      : 'border-yellow-200 bg-yellow-50 hover:border-yellow-300 hover:shadow-md'
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    {suggestion.thumbnail && suggestion.thumbnail.trim() !== '' ? (
                      <img
                        src={suggestion.thumbnail}
                        alt={suggestion.title}
                        className="w-32 h-20 object-cover rounded-lg"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-32 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex flex-col items-center justify-center ${suggestion.thumbnail && suggestion.thumbnail.trim() !== '' ? 'hidden' : ''}`}>
                      <Music className="w-8 h-8 text-purple-500 mb-1" />
                      <p className="text-xs text-purple-600 font-semibold px-2 text-center line-clamp-2">
                        {suggestion.title.split('-')[0].trim().slice(0, 20)}
                      </p>
                    </div>
                    {selectedIds.has(`suggestion-${index}`) && (
                      <div className="absolute inset-0 bg-purple-500 bg-opacity-70 rounded-lg flex items-center justify-center">
                        <Check className="w-8 h-8 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                      {suggestion.title}
                    </h3>
                    {suggestion.channelTitle && (
                      <p className="text-sm text-gray-600 mb-2">
                        {suggestion.channelTitle}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {suggestion.aiReason || suggestion.description}
                    </p>
                    {!hasValidVideo && (
                      <p className="text-xs text-orange-600 mt-1 font-medium">
                        {language === 'ko'
                          ? '⚠️ YouTube 영상을 찾을 수 없습니다 - 선택 후 수동으로 URL을 입력할 수 있습니다'
                          : '⚠️ No YouTube video found - You can manually add URL after selection'}
                      </p>
                    )}
                  </div>
                </button>
              );
              })}
            </div>
          )}
        </div>

        {suggestions.length > 0 && (
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {language === 'ko'
                  ? `${selectedIds.size}개 선택됨`
                  : `${selectedIds.size} selected`}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  {language === 'ko' ? '취소' : 'Cancel'}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={selectedIds.size === 0}
                  className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {language === 'ko'
                    ? `${selectedIds.size}개 드롭하기`
                    : `Drop ${selectedIds.size} pins`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
